import { Avatar, Box, Typography, useTheme, Select, MenuItem } from "@mui/material";
import { useMetrics } from "../../../shared/context/metricsContext";
import { useEffect, useState } from "react";
import { useUtiles } from "../../../shared/context/utilesContext";
import { Doughnut } from 'react-chartjs-2';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(ChartDataLabels);

export default function TechnicalMetrics() {
    const { tasksByTechnician, loadingTasksByTechnician, getTasksByTechnician, currentLocation } = useMetrics();
    const [selectedTechnician, setSelectedTechnician] = useState(null);
    const [filteredTechnicians, setFilteredTechnicians] = useState([]);

    const { getFirstNameAndLastName } = useUtiles();

    const theme = useTheme();

    const estados = [
        { key: 'in_process', name: 'En Proceso', color: '#3498db' },
        { key: 'review', name: 'En Revisión', color: '#f39c12' },
        { key: 'completed', name: 'Completadas', color: '#17a589' },
        { key: 'cancel', name: 'Canceladas', color: '#566573' }
    ];

    const MONTHS = [
        { full: 'enero', short: 'Ene', value: 1 },
        { full: 'febrero', short: 'Feb', value: 2 },
        { full: 'marzo', short: 'Mar', value: 3 },
        { full: 'abril', short: 'Abr', value: 4 },
        { full: 'mayo', short: 'May', value: 5 },
        { full: 'junio', short: 'Jun', value: 6 },
        { full: 'julio', short: 'Jul', value: 7 },
        { full: 'agosto', short: 'Ago', value: 8 },
        { full: 'septiembre', short: 'Sep', value: 9 },
        { full: 'octubre', short: 'Oct', value: 10 },
        { full: 'noviembre', short: 'Nov', value: 11 },
        { full: 'diciembre', short: 'Dic', value: 12 }
    ];

    const [selectMonth, setSelectMonth] = useState(new Date().getMonth() + 1);
    useEffect(() => {
        getTasksByTechnician({ month: selectMonth });
    }, [selectMonth]);

    useEffect(() => {
        getTasksByTechnician();
    }, []);

    // Filter technicians based on currentLocation and update state
    useEffect(() => {
        if (tasksByTechnician && tasksByTechnician.length > 0 && currentLocation) {
            const locationData = tasksByTechnician.find(loc => loc.location.name === currentLocation.name);
            if (locationData) {
                const technicians = locationData.technicians;
                setFilteredTechnicians(technicians);
                // Always set first technician when location changes
                setSelectedTechnician(technicians[0]);
            } else {
                setFilteredTechnicians([]);
                setSelectedTechnician(null);
            }
        }
    }, [tasksByTechnician, currentLocation]);

    const handleTechnicianClick = (technician) => {
        if (technician.name === selectedTechnician?.name) return;
        setSelectedTechnician(selectedTechnician?.name === technician.name ? null : technician);
    };

    const chartData = selectedTechnician ? {
        labels: estados.map(estado => estado.name),
        datasets: [{
            data: estados.map(estado => selectedTechnician.ots[estado.key] || 0),
            backgroundColor: estados.map(estado => estado.color),
            borderWidth: 0
        }]
    } : null;

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    color: theme.palette.text.primary,
                    font: {
                        size: 14,
                        weight: 'bold'
                    },
                    usePointStyle: true,
                    padding: 20,
                    boxWidth: 10,
                    boxHeight: 10
                }
            },
            datalabels: {
                color: '#fff',
                formatter: (value, ctx) => {
                    const dataset = ctx.chart.data.datasets[0];
                    const total = dataset.data.reduce((acc, data) => acc + data, 0);
                    const percentage = ((value / total) * 100).toFixed(1);
                    return percentage > 0 ? `${value}\n(${percentage}%)` : '';
                },
                font: {
                    weight: 'bold',
                    size: 16
                },
                textAlign: 'center'
            }
        }
    };

    return (
        <Box sx={{
            width: '100%',
            backgroundColor: theme.palette.background.paper,
            padding: theme.spacing(2),
            boxShadow: theme.palette.shadow.default,
            borderRadius: theme.spacing(1),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <Typography variant="h6" sx={{
                fontWeight: 'bold',
                fontSize: { xs: '0.9rem', sm: '1rem' },
                color: theme.palette.text.primary,
                mb: 2
            }}>Cantidad de OTs por técnico</Typography>

            <Box sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
            }}>
                <Avatar src={selectedTechnician?.avatar || selectedTechnician?.name} sx={{
                    width: 50,
                    height: 50,
                    mr: 1
                }} />
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-center',
                    justifyContent: 'center',
                    ml: 1
                }}>
                    <Typography variant="body1" sx={{
                        fontWeight: 'bold',
                        fontSize: { xs: '0.8em', sm: '0.9em' },
                        color: theme.palette.text.primary,
                    }}>{(selectedTechnician?.name || 'Sin técnico').toUpperCase()}</Typography>
                    <Typography variant="body1" sx={{
                        fontWeight: 'normal',
                        fontSize: { xs: '11px', sm: '12px' },
                        color: theme.palette.text.primary,
                    }}>{(selectedTechnician?.location?.name || 'Sin ubicación').toUpperCase()}</Typography>
                </Box>
            </Box>

            {selectedTechnician && (
                <Box sx={{
                    width: '100%',
                    height: 440,
                    my: 1,
                    display: 'flex',
                    justifyContent: 'center'
                }}>
                    <Doughnut data={chartData} options={chartOptions} />
                </Box>
            )}

            <Box sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                overflowX: 'auto',
                mb: 2
            }}>
                {filteredTechnicians.map((technician) => (
                    <Typography
                        key={technician.name}
                        variant="body1"
                        onClick={() => handleTechnicianClick(technician)}
                        sx={{
                            fontWeight: 'bold',
                            fontSize: { xs: '0.8rem', sm: '0.9rem' },
                            color: selectedTechnician?.name === technician.name
                                ? "white"
                                : theme.palette.text.primary,
                            backgroundColor: selectedTechnician?.name === technician.name
                                ? theme.palette.primary.main
                                : theme.palette.background.paper,
                            px: 2,
                            py: 1,
                            borderRadius: theme.spacing(3),
                            mb: 1,
                            mr: 1,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                            }
                        }}
                    >
                        {(getFirstNameAndLastName(technician.name)).toUpperCase()}
                    </Typography>
                ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', gap: 1, width: '100%', mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Seleccione el mes:
                </Typography>
                <Select
                    value={selectMonth}
                    onChange={(e) => setSelectMonth(e.target.value)}
                    size="small"
                >
                    {MONTHS.map(month => (
                        <MenuItem key={month.value} value={month.value}>{month.full}</MenuItem>
                    ))}
                </Select>
            </Box>
        </Box>
    );
}